import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line, RiDownloadLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  InfraCodeBlock,
  InfraDivider,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const RedisOSSSentinelInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';

  const primaryIp = annotations['redis.infrastructure/primary-ip'];
  const replica1Ip = annotations['redis.infrastructure/replica-1-ip'];
  const replica2Ip = annotations['redis.infrastructure/replica-2-ip'];
  const sentinel1Ip = annotations['redis.infrastructure/sentinel-1-ip'];
  const sentinel2Ip = annotations['redis.infrastructure/sentinel-2-ip'];
  const sentinel3Ip = annotations['redis.infrastructure/sentinel-3-ip'];
  const masterName = annotations['redis.infrastructure/master-name'];
  const redisPort = annotations['redis.infrastructure/redis-port'];
  const sentinelPort = annotations['redis.infrastructure/sentinel-port'];
  const connectionString = annotations['redis.infrastructure/connection-string'];
  const sentinelConnectionString = annotations['redis.infrastructure/sentinel-connection-string'];
  const sshPrivateKeyEncoded = annotations['redis.infrastructure/ssh-private-key'];

  const hasRedisOSSSentinelInfo =
    templateType === 'aws-redis-oss-sentinel' && (primaryIp || sentinel1Ip);

  if (!hasRedisOSSSentinelInfo) {
    return null;
  }

  const downloadSSHKey = () => {
    if (!sshPrivateKeyEncoded) return;

    try {
      const sshKey = atob(sshPrivateKeyEncoded);
      const blob = new Blob([sshKey], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'redis-sentinel-key.pem';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download SSH key:', error);
    }
  };

  const sshPrimaryCmd = `chmod 400 redis-sentinel-key.pem\nssh -i redis-sentinel-key.pem ec2-user@${primaryIp}`;
  const sshSentinelCmd = `ssh -i redis-sentinel-key.pem ec2-user@${sentinel1Ip}`;
  const redisCliCmd = `redis-cli -h ${primaryIp} -p ${redisPort} -a <password>`;
  const sentinelCliCmd = `redis-cli -h ${sentinel1Ip} -p ${sentinelPort}\nSENTINEL masters\nSENTINEL get-master-addr-by-name ${masterName}`;
  const failoverCmd = `redis-cli -h ${sentinel1Ip} -p ${sentinelPort}\nSENTINEL failover ${masterName}`;

  return (
    <InfoCard
      title="Redis OSS with Sentinel HA"
      icon={<RiDatabase2Line size={20} />}
    >
      <InfraStack>
        <InfraSection title="☁️ Cluster Topology">
          <InfraField
            label="Architecture:"
            value={
              <InfraTagRow>
                <InfraTag>1 Primary + 2 Replicas + 3 Sentinels</InfraTag>
              </InfraTagRow>
            }
            mono={false}
          />
          {masterName && <InfraField label="Master Name:" value={masterName} />}
          {redisPort && <InfraField label="Redis Port:" value={redisPort} />}
          {sentinelPort && <InfraField label="Sentinel Port:" value={sentinelPort} />}
        </InfraSection>

        <InfraSection title="🖥️ Redis Nodes">
          {primaryIp && (
            <>
              <Text variant="title-small">Primary Node</Text>
              <CopyableValue label="Public IP:" value={primaryIp} />
            </>
          )}
          {replica1Ip && (
            <>
              <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                Replica 1
              </Text>
              <CopyableValue label="Public IP:" value={replica1Ip} />
            </>
          )}
          {replica2Ip && (
            <>
              <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                Replica 2
              </Text>
              <CopyableValue label="Public IP:" value={replica2Ip} />
            </>
          )}
        </InfraSection>

        <InfraSection title="🛡️ Sentinel Nodes">
          {sentinel1Ip && (
            <>
              <Text variant="title-small">Sentinel 1</Text>
              <CopyableValue label="Public IP:" value={sentinel1Ip} />
            </>
          )}
          {sentinel2Ip && (
            <>
              <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                Sentinel 2
              </Text>
              <CopyableValue label="Public IP:" value={sentinel2Ip} />
            </>
          )}
          {sentinel3Ip && (
            <>
              <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                Sentinel 3
              </Text>
              <CopyableValue label="Public IP:" value={sentinel3Ip} />
            </>
          )}
        </InfraSection>

        <InfraDivider />
        <InfraSection title="🔌 Connection Information">
          {connectionString && (
            <>
              <Text variant="title-small">Redis Connection String:</Text>
              <div style={{ position: 'relative' }}>
                <InfraCodeBlock variant="light">{connectionString}</InfraCodeBlock>
                <div style={{ position: 'absolute', top: 4, right: 4 }}>
                  <CopyButton text={connectionString} />
                </div>
              </div>
            </>
          )}
          {sentinelConnectionString && (
            <>
              <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                Sentinel Connection String:
              </Text>
              <div style={{ position: 'relative' }}>
                <InfraCodeBlock variant="light">{sentinelConnectionString}</InfraCodeBlock>
                <div style={{ position: 'absolute', top: 4, right: 4 }}>
                  <CopyButton text={sentinelConnectionString} />
                </div>
              </div>
            </>
          )}
        </InfraSection>

        {sshPrivateKeyEncoded && (
          <>
            <InfraDivider />
            <InfraSection title="🔑 SSH Access">
              <Text variant="body-medium">
                Download the SSH private key to access the instances:
              </Text>
              <Button variant="primary" size="small" onPress={downloadSSHKey}>
                <RiDownloadLine size={16} />
                Download SSH Key
              </Button>

              <Text variant="title-small" style={{ marginTop: '1rem' }}>
                SSH to Primary Node:
              </Text>
              <div style={{ position: 'relative' }}>
                <InfraCodeBlock variant="light">{sshPrimaryCmd}</InfraCodeBlock>
                <div style={{ position: 'absolute', top: 4, right: 4 }}>
                  <CopyButton text={sshPrimaryCmd} />
                </div>
              </div>

              <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                SSH to Sentinel Node:
              </Text>
              <div style={{ position: 'relative' }}>
                <InfraCodeBlock variant="light">{sshSentinelCmd}</InfraCodeBlock>
                <div style={{ position: 'absolute', top: 4, right: 4 }}>
                  <CopyButton text={sshSentinelCmd} />
                </div>
              </div>
            </InfraSection>
          </>
        )}

        <InfraDivider />
        <InfraSection title="🚀 Quick Start">
          <Text variant="title-small">1. Connect to Redis (direct connection):</Text>
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">
              {`redis-cli -h ${primaryIp || '<primary-ip>'} -p ${redisPort || '6379'} -a <password>`}
            </InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={redisCliCmd} />
            </div>
          </div>

          <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
            2. Connect to Sentinel:
          </Text>
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">
              {`redis-cli -h ${sentinel1Ip || '<sentinel-ip>'} -p ${sentinelPort || '26379'}\nSENTINEL masters\nSENTINEL get-master-addr-by-name ${masterName || 'mymaster'}`}
            </InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={sentinelCliCmd} />
            </div>
          </div>

          <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
            3. Test Failover (from Sentinel):
          </Text>
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">
              {`redis-cli -h ${sentinel1Ip || '<sentinel-ip>'} -p ${sentinelPort || '26379'}\nSENTINEL failover ${masterName || 'mymaster'}`}
            </InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={failoverCmd} />
            </div>
          </div>

          <Text variant="body-small" style={{ marginTop: '0.75rem', display: 'block' }}>
            💡 <strong>Note:</strong> Sentinel provides automatic failover. If the primary fails,
            Sentinel will promote one of the replicas to primary.
          </Text>
        </InfraSection>
      </InfraStack>
    </InfoCard>
  );
};
